<template>
    <div class="dashboard_classification">
        <div class="main_div">
            <ClassificationHeader
                ref="classificationHeader"
                read-only
                show-download
                title="Libro Mayor"
                description="Documentos del libro mayor (ERP) y su clasificación — solo lectura."
                :typeOptions="typeOptions"
                :periodDataSelect="periodDataSelect"
                :localTypeFiles="[]"
                :selectedType="selectedType"
                :selectedRange="selectedRange"
                :searchFilter="searchFilter"
                :filterTableHasAccrual="filterTableHasAccrual"
                :allFilterCategories="allFilterCategories"
                :allAccountsOnDocs="allAccountsOnDocs"
                :isDownloadingFile="isDownloadingErp"
                hide-unclassified-filter
                @update:selectedType="onSelectedType"
                @update:selectedRange="onSelectedRange"
                @update:searchFilter="(v) => searchFilter = v"
                @update:filterTableHasAccrual="(v) => filterTableHasAccrual = v"
                @selected-categories="updateSelectedCategories"
                @selected-accounts="updateSelectedAccounts"
                @download="downloadErp"
            >
                <!-- Sub-filtro EE.RR / Balance, centrado a la altura de los
                    selects del header. Segmented control del dialecto ledger:
                    pista con lavado teal y thumb de papel que desliza entre
                    los dos estados (ver .scope_switch). -->
                <template #center-actions>
                    <div class="scope_switch"
                        role="group"
                        aria-label="Tipo de cuentas"
                        :class="{ 'is-balance': accountScope === 'balance' }">
                        <span class="scope_thumb" aria-hidden="true"></span>
                        <button v-for="scope in accountScopeOptions" :key="scope.id"
                            type="button"
                            class="scope_btn"
                            :class="{ 'is-active': accountScope === scope.id }"
                            :aria-pressed="accountScope === scope.id ? 'true' : 'false'"
                            @click="setAccountScope(scope.id)">
                            {{ scope.label }}
                        </button>
                    </div>
                </template>
            </ClassificationHeader>

            <div class="underHeader">
                <CounterpartTable
                    ref="counterpartTable"
                    class="mb-5"
                    read-only
                    :entries="getPaginatedList"
                    :categories="categories"
                    :accountFilters="accountFilters"
                    :categoriesFilter="categoriesFilter"
                    :sortColumn="sortColumn"
                    :sortDirection="sortDirection"
                    :siiSyncing="false"
                    :maxHeight="tableMaxHeight"
                    :showReportLink="hasFinancialReport"
                    @sort="changeOrderGeneric"
                    @open="openLedgerDocsModal"
                    @open-report="openReport"
                    @clear-filter="clearFiltered"
                    @delete-filter="deleteItemFilter"
                />
                <Pagination ref="pagination" v-model="actualPage" :maxPages="maxPages" />
            </div>
        </div>
    </div>
</template>

<script>
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/900.css";

import Pagination from "@/components/generics/Pagination";
import CounterpartTable from "@/components/classification/CounterpartTable.vue";
import ClassificationHeader from "@/components/classification/ClassificationHeader.vue";
import ClassifyAccountAttributes from "@/components/modals/ClassifyAccountAttributes";
import { sortCounterparts } from "@/utils/classificationSort";
import { computeVisibleEntries } from "@/utils/classificationFilters";
import { TAB_IDS } from "@/views/financialReports/config/tabs";
import { downloadLedgerFile } from "@/api/carteraAnalista";
import { notificate } from "@/utils/message";
import { mapActions, mapState } from "vuex";

// Igual que en DashboardClassificator: la tabla llena el alto libre del viewport
// y scrollea internamente (sin scroll de página). Cap medido en runtime.
const TABLE_MIN_HEIGHT = 220;
const TABLE_BELOW_GAP = 48;

// Nivel 1 del plan de cuentas → tab del reporte financiero más aproximado. El
// ícono de gráfico de cada fila lleva ahí. Sin match → Estado de Resultados.
const LEVEL_1_TO_REPORT_TAB = {
    "Ventas": TAB_IDS.VENTAS,
    "Costos": TAB_IDS.COSTOS,
    "Gastos": TAB_IDS.GASTOS,
    "Activos": TAB_IDS.ACTIVOS,
    "No Operacional": TAB_IDS.NO_OPER,
};

// Tabs del reporte que tienen modal de detalle de contraparte (Ventas/Costos/
// Gastos). Sólo en estos dejamos un pendingDrill para auto-abrir la contraparte.
const REPORT_TABS_WITH_COUNTERPART_DETAIL = [
    TAB_IDS.VENTAS, TAB_IDS.COSTOS, TAB_IDS.GASTOS,
];

// Sub-filtro EE.RR / Balance del mayor (param `account_scope` del backend):
// "eerr" lista contrapartes SIN cuentas de balance (las mixtas pertenecen a
// Balance); "balance" deja sólo los docs de cuentas de balance, con totales
// acotados a ellos. Mismo criterio dentro del modal de documentos.
const ACCOUNT_SCOPE_OPTIONS = [
    { id: "eerr", label: "EE.RR" },
    { id: "balance", label: "Balance" },
];

// Vista de Libro Mayor (ERP), SOLO LECTURA. Reusa el módulo `classification` y
// los componentes del clasificador en modo readOnly, pero pide al backend sólo
// los documentos del mayor (`ledger_only: true`) y no permite editar: sin upload,
// sin deep-link, sin árbol editable (el detalle por documento abre un visor
// read-only). Es mutuamente excluyente con el clasificador (ambos `v-if` en
// Dashboard.vue), así que refetchea al montar y no coexisten.
export default {
    name: "DashboardErp",
    components: {
        Pagination,
        CounterpartTable,
        ClassificationHeader,
    },
    data() {
        return {
            countDirectionOrder: 0,
            sortColumn: "order",
            sortDirection: "asc",
            modalDefault: {
                parent: this,
                width: "99%",
                hasModalCard: true,
                canCancel: ["escape", "outside"],
                customClass: "center-always-modal",
            },
            searchFilter: "",
            filterTableHasAccrual: "",
            actualPage: 1,
            itemsPerPage: 20,
            allAccountsOnDocs: [],
            allFilterCategories: [],
            accountFilters: [],
            categoriesFilter: [],
            isDownloadingErp: false,
            tableMaxHeight: null,
            // Sub-filtro activo (EE.RR por defecto). El backend filtra por él,
            // así que cada cambio refetchea el listado.
            accountScope: "eerr",
            accountScopeOptions: ACCOUNT_SCOPE_OPTIONS,
            // Comparte el módulo Vuex `classification` con DashboardClassificator.
            // Si esta vista se destruye (navegación al clasificador), su cadena
            // async no debe disparar un getAllCounterparts tardío que pise el otro
            // listado. Ver guard en getAllPaginatedCounterparts.
            isActive: true,
        };
    },
    mounted() {
        // Comparte el slot de contrapartes con el clasificador: limpiar al montar
        // evita mostrar las del clasificador hasta que llega el fetch del mayor.
        this.clearCounterparts();
        this.initClassificationFilters();
        this.getAllAccounts().then(() => {
            this.onSelectedRange("last12");
        });

        this.changeColumn("total_3_months");
        this.sortData("total_3_months");
        this.changeColumn("total_3_months");
        this.sortData("total_3_months");

        this.$nextTick(this.measureTableHeight);
        window.addEventListener("resize", this.measureTableHeight);
        if (typeof ResizeObserver !== "undefined") {
            this.headerResizeObserver = new ResizeObserver(() => this.measureTableHeight());
            const headerEl = this.$refs.classificationHeader && this.$refs.classificationHeader.$el;
            if (headerEl) this.headerResizeObserver.observe(headerEl);
        }
    },
    beforeDestroy() {
        this.isActive = false;
        window.removeEventListener("resize", this.measureTableHeight);
        if (this.headerResizeObserver) {
            this.headerResizeObserver.disconnect();
            this.headerResizeObserver = null;
        }
    },
    methods: {
        ...mapActions("classification", ["getCategories", "getAllAccounts",
            "getAllCounterparts", "setSelectedPeriodCounterparts", "setOrderedCounterparts",
            "orderCounterparts", "updateSelectedType", "initClassificationFilters",
            "clearCounterparts"]),
        // Baja TODA la data del mayor como .xlsx re-subible. El backend la
        // regenera desde los documentos persistidos (mismo esquema que el
        // importador); disparamos la descarga desde el blob de la respuesta.
        async downloadErp() {
            if (this.isDownloadingErp) return;
            this.isDownloadingErp = true;
            notificate(
                `<strong>Procesando!</strong><br>Generando la data del ERP...`,
                "is-info is-light"
            );
            try {
                const { data } = await downloadLedgerFile();
                const url = window.URL.createObjectURL(new Blob([data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "Data ERP.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                notificate(
                    `<strong>Éxito!</strong><br>Descarga iniciada.`,
                    "is-success is-light"
                );
            } catch (error) {
                console.error("Error downloading ERP data:", error);
                notificate(
                    `<strong>Error!</strong><br>No se pudo descargar la data del ERP.`,
                    "is-danger is-light"
                );
            } finally {
                this.isDownloadingErp = false;
            }
        },
        measureTableHeight() {
            const tableEl = this.$refs.counterpartTable && this.$refs.counterpartTable.$el;
            if (!tableEl) return;
            const top = tableEl.getBoundingClientRect().top;
            const paginationEl = this.$refs.pagination && this.$refs.pagination.$el;
            const paginationH = paginationEl ? paginationEl.offsetHeight : 0;
            const available = window.innerHeight - top - paginationH - TABLE_BELOW_GAP;
            this.tableMaxHeight = Math.max(TABLE_MIN_HEIGHT, Math.round(available));
        },
        updateSelectedAccounts(accountsSelected) {
            this.accountFilters = [...accountsSelected];
            this.allFiltersToEntries();
        },
        updateSelectedCategories(categoriesSelected) {
            this.categoriesFilter = [...categoriesSelected];
            this.allFiltersToEntries();
        },
        clearFiltered(type) {
            if (type == "accounts") {
                this.$refs.classificationHeader.clearAccountFilter();
            } else {
                this.$refs.classificationHeader.clearCategoryFilter();
            }
            this.allFiltersToEntries();
        },
        deleteItemFilter(type, item) {
            if (type == "accounts") {
                this.$refs.classificationHeader.deleteAccountItem(item);
            } else {
                this.$refs.classificationHeader.deleteCategoryItem(item);
            }
        },
        onSelectedType(newValue) {
            const optionSelected = this.typeOptions.find((o) => o.value == newValue);
            this.updateSelectedType(optionSelected);
            this.allFiltersToEntries();
        },
        onSelectedRange(newValue) {
            const optionSelected = this.periodDataSelect.find((o) => o.value == newValue);
            this.setSelectedPeriodCounterparts(optionSelected);
            this.getAllPaginatedCounterparts();
        },
        setAccountScope(scope) {
            if (scope === this.accountScope) return;
            this.accountScope = scope;
            this.actualPage = 1;
            this.getAllPaginatedCounterparts();
        },
        resetMsgs() {
            this.sortColumn = "total_3_months";
            this.sortDirection = "desc";
        },
        sortData(column, subtype = null, reset_msg = true) {
            if (reset_msg) {
                this.resetMsgs();
            }
            let col_name = column;
            if (subtype != null) {
                col_name = "attribute_" + subtype;
            }
            if (this.countDirectionOrder == 0) {
                this.sortColumn = "order";
                this.sortDirection = "asc";
            } else if (this.countDirectionOrder == 1) {
                this.sortColumn = col_name;
                this.sortDirection = "asc";
            } else if (this.countDirectionOrder == 2) {
                this.sortColumn = col_name;
                this.sortDirection = "desc";
            }
        },
        changeColumn(column, subtype = null) {
            let column_name = column;
            if (subtype) {
                column_name = column + "_" + subtype;
            }
            if (this.sortColumn != column_name) {
                this.countDirectionOrder = 0;
                this.sortColumn = column;
                if (subtype) {
                    this.sortColumn = column + "_" + subtype;
                }
            }
            this.countDirectionOrder = (this.countDirectionOrder + 1) % 3;
        },
        actualSort(subtype = null) {
            this.setOrderedCounterparts(
                sortCounterparts(this.allOrderedCounterparts, this.sortColumn, this.sortDirection, subtype)
            );
        },
        changeOrderGeneric(column, subtype = null) {
            this.changeColumn(column, subtype);
            this.sortData(column, subtype, false);
            this.actualSort(subtype);
        },
        async getAllPaginatedCounterparts() {
            // Si la vista ya se destruyó (navegación al clasificador), no dispares el
            // fetch: pisaría las contrapartes del clasificador en el store compartido.
            if (!this.isActive) return;
            const request = {
                from: 0,
                to: 999999,
                from_day: this.selectedPeriodCounterparts.from,
                to_day: this.selectedPeriodCounterparts.to,
                ledger_only: true,
                account_scope: this.accountScope,
            };
            await this.getAllCounterparts(request);
            this.actualSort(null);
            this.allFiltersToEntries();
            const allOrdered = JSON.parse(JSON.stringify(this.allOrderedCounterparts));
            this.orderCounterparts(allOrdered);
        },
        openLedgerDocsModal(contraparte) {
            // Mismo modal "drawer" del clasificador (ClassifyAccountAttributes)
            // pero en read-only y pidiendo sólo el mayor: oculta el árbol de
            // prorrateo/clasificación y muestra la tabla de documentos (con su
            // devengo) idéntica a la del clasificador.
            this.$buefy.modal.open({
                ...this.modalDefault,
                component: ClassifyAccountAttributes,
                customClass: "classify_modal_width",
                props: {
                    rangeOptions: this.periodDataSelect,
                    rangeFromMainScreen: this.selectedRange,
                    types: this.categories,
                    counterpart: contraparte,
                    readOnly: true,
                    ledgerOnly: true,
                    // El modal filtra sus documentos con el mismo sub-filtro
                    // EE.RR/Balance activo en la lista.
                    accountScope: this.accountScope,
                },
            });
        },
        // Nivel 1 de la primera cuenta madre (level_2) de la contraparte. El plan
        // de cuentas (allAccounts) trae level_1/2/3; resolvemos por level_2.
        reportTabForEntry(entry) {
            const motherAccounts = entry.calculatedMotherAccounts || [];
            for (const level2 of motherAccounts) {
                const account = this.allAccounts.find((a) => a.level_2 == level2);
                if (account && LEVEL_1_TO_REPORT_TAB[account.level_1]) {
                    return LEVEL_1_TO_REPORT_TAB[account.level_1];
                }
            }
            return TAB_IDS.RESULTADOS;
        },
        openReport(entry) {
            // Lleva al tab del reporte financiero más aproximado a la cuenta de la
            // contraparte. El cambio de `section` lo escucha Dashboard.vue (watcher
            // de $route.query.section) y `tab` lo lee DashboardReports.
            const tab = this.reportTabForEntry(entry);
            // Si el tab destino tiene detalle de contraparte, dejamos un pendingDrill
            // (mismo mecanismo cross-tab del P&L) para que al montar abra el modal de
            // ESTA contraparte (resuelto por alias). En otros tabs sólo navegamos.
            this.$store.commit(
                "reportsFilters/SET_PENDING_DRILL",
                REPORT_TABS_WITH_COUNTERPART_DETAIL.includes(tab)
                    ? { tab, counterpartAlias: entry.alias, counterpartType: entry.counterpart_type }
                    : null,
            );
            this.$router
                .push({ query: { ...this.$route.query, section: "reports", tab } })
                .catch((err) => {
                    if (err.name !== "NavigationDuplicated") throw err;
                });
        },
        allFiltersToEntries() {
            const { entries, usedAccounts, filterCategories } = computeVisibleEntries(
                this.allOrderedCounterparts,
                {
                    selectedType: this.selectedType,
                    searchFilter: this.searchFilter,
                    filterTable: "",
                    filterTableHasAccrual: this.filterTableHasAccrual,
                    accountFilters: this.accountFilters,
                    categoriesFilter: this.categoriesFilter,
                    categories: this.categories,
                }
            );
            this.setOrderedCounterparts(entries);
            this.allFilterCategories = filterCategories;
            this.allAccountsOnDocs = this.allAccounts.filter(
                (account) => usedAccounts.includes(account.level_3)
            );
            if (this.getPaginatedList.length == 0) {
                this.actualPage = 1;
            }
        },
    },
    computed: {
        ...mapState("classification", ["periodDataSelect", "selectedPeriodCounterparts",
            "categories", "allAccounts", "allOrderedCounterparts", "selectedType",
            "selectedRange", "typeOptions"]),
        ...mapState("userAuth", ["user"]),
        // El ícono de gráfico (link al reporte) sólo se muestra si el usuario
        // tiene acceso al reporte financiero.
        hasFinancialReport: function () {
            return !!(this.user && this.user.has_financial_report);
        },
        filteredListCounterparts: function () {
            return this.allOrderedCounterparts.filter((entry) => entry.visible);
        },
        maxPages: function () {
            return Math.ceil(this.filteredListCounterparts.length / this.itemsPerPage) || 1;
        },
        getPaginatedList: function () {
            const startIndex = (this.actualPage - 1) * this.itemsPerPage;
            const endIndex = this.actualPage * this.itemsPerPage;
            return this.filteredListCounterparts.slice(startIndex, endIndex);
        },
    },
    watch: {
        searchFilter() {
            this.allFiltersToEntries();
        },
        filterTableHasAccrual() {
            this.allFiltersToEntries();
        },
    },
};
</script>

<style lang="scss" scoped>
.dashboard_classification {
    // Mismo dialecto ledger que DashboardClassificator (paper/rule/ink/accent).
    // Los hijos (ClassificationHeader, CounterpartTable) heredan estas vars.
    --paper: #ffffff;
    --paper-soft: #fafbfc;
    --rule: rgba(16, 24, 40, 0.08);
    --rule-strong: rgba(16, 24, 40, 0.14);
    --ink: #1d2939;
    --ink-muted: #475467;
    --ink-faint: #98a2b3;
    --accent: #0d5154;
    --accent-wash: rgba(13, 81, 84, 0.06);
    // Verde profundo de los selects del header (tema oscuro de
    // SelectComponent: background #002e33, hover #185555) — lo usa el estado
    // activo del sub-filtro EE.RR/Balance para leerse como parte del mismo
    // cluster de controles.
    --accent-deep: #002e33;
    --accent-deep-hover: #185555;
    --hover: rgba(16, 24, 40, 0.03);
    --radius: 6px;

    background-color: var(--paper-soft);
    font-family: Inter, "Open Sans", sans-serif;
}

.underHeader {
    position: relative;
}

// Sub-filtro EE.RR/Balance del header. Segmented control emparentado con los
// selects vecinos (Tipo Contraparte / Rango de tiempo): pista de papel con el
// mismo borde, y el estado activo es una ficha del MISMO verde profundo
// (--accent-deep) que DESLIZA entre las dos celdas — el color no se swapea,
// la ficha se mueve. Labels en mayúsculas con tracking, como los encabezados
// de columna de un registro contable. Va scoped en la vista porque se inyecta
// por slot (los estilos scoped de ClassificationHeader no alcanzan al markup
// del slot).
.scope_switch {
    position: relative;
    display: grid;
    // Celdas iguales: el thumb mide exactamente 50% y calza en ambas.
    grid-template-columns: 1fr 1fr;
    padding: 3px;
    background: var(--paper);
    border: 1px solid var(--rule-strong);
    border-radius: 8px;
    width: fit-content;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.scope_thumb {
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: 3px;
    width: calc(50% - 3px);
    background: var(--accent-deep);
    border-radius: 5px;
    box-shadow: 0 1px 2px rgba(0, 46, 51, 0.25);
    transition: transform 0.22s cubic-bezier(0.2, 0, 0, 1);
}

.scope_switch.is-balance .scope_thumb {
    transform: translateX(100%);
}

.scope_btn {
    position: relative;
    z-index: 1;
    padding: 9px 24px;
    border: none;
    background: transparent;
    border-radius: 5px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    color: var(--ink-muted);
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
        color: var(--accent-deep);
    }

    &.is-active {
        color: var(--paper);

        // El hover del activo aclara la ficha, como el hover de los selects
        // (#185555). La ficha es un hermano previo: :has() sube el estado.
        // Sin soporte de :has(), el hover simplemente no aclara — inofensivo.
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--paper), 0 0 0 4px var(--accent);
    }
}

.scope_switch:has(.scope_btn.is-active:hover) .scope_thumb {
    background: var(--accent-deep-hover);
}

// En pantallas chicas el control baja a su propia fila (grid del header);
// menos padding para que respire junto a la tabla.
@media (max-width: 1280px) {
    .scope_btn {
        padding: 7px 20px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .scope_thumb {
        transition: none;
    }

    .scope_btn {
        transition: none;
    }
}

.main_div {
    padding-left: clamp(16px, 2vw, 32px);
    padding-right: clamp(16px, 5vw, 90px);
}
</style>
